import { useEffect, useMemo, useState } from "react";
import { Alert, Box, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton } from "../../../components/form";
import {
  forecastPlanService,
  type ForecastAnalysisPlan,
} from "../../../services/forecastPlanService";
import type { ChatOrchestrationWebSocketPayload } from "../../../services/chatService";
import ForecastResultView from "./ForecastResultView";
import { parseForecastResult } from "../../../utils/forecastResultModel";

function resolvePlan(payload: ChatOrchestrationWebSocketPayload): ForecastAnalysisPlan | null {
  if (payload.forecastPlan && typeof payload.forecastPlan === "object") {
    return payload.forecastPlan as ForecastAnalysisPlan;
  }
  const result = payload.result;
  if (result && typeof result === "object") {
    const record = result as Record<string, unknown>;
    if (record.forecastPlan && typeof record.forecastPlan === "object") {
      return record.forecastPlan as ForecastAnalysisPlan;
    }
    if (record.id && (record.steps || record.kvkkNotice || record.method || record.intent)) {
      return record as ForecastAnalysisPlan;
    }
  }
  return null;
}

export default function ForecastPlanPanel({
  message,
  payload,
}: {
  message?: string;
  payload: ChatOrchestrationWebSocketPayload;
}) {
  const { t } = useTranslation();
  const initial = useMemo(() => resolvePlan(payload), [payload]);
  const [plan, setPlan] = useState<ForecastAnalysisPlan | null>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPlan(initial);
  }, [initial]);

  useEffect(() => {
    const id = initial?.id;
    if (!id) return;
    let cancelled = false;
    void forecastPlanService
      .get(id)
      .then((fresh) => {
        if (!cancelled) setPlan(fresh);
      })
      .catch(() => {
        /* keep chat payload */
      });
    return () => {
      cancelled = true;
    };
  }, [initial?.id]);

  const status = String(plan?.status || "PENDING").toUpperCase();
  const pending = status === "PENDING";
  const completed = status === "COMPLETED";
  const rejected = status === "REJECTED";
  const failed = status === "FAILED";
  const isDocumentSample = String(plan?.intent || "").toUpperCase() === "DOCUMENT_SAMPLE";

  const resultModel = useMemo(() => {
    if (!plan || !completed || isDocumentSample) return null;
    return (
      parseForecastResult(plan.analysisPayload) ??
      parseForecastResult({
        response: plan.aiNarrative,
        data: plan.analysisPayload,
      })
    );
  }, [plan, completed, isDocumentSample]);

  if (!plan) {
    return <Alert severity="warning">{t("chat.forecast.planMissing")}</Alert>;
  }

  const onApprove = async () => {
    if (!plan.id) return;
    setBusy(true);
    setError(null);
    try {
      const next = await forecastPlanService.approve(plan.id);
      setPlan(next);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("chat.forecast.approveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const onReject = async () => {
    if (!plan.id) return;
    setBusy(true);
    setError(null);
    try {
      const next = await forecastPlanService.reject(plan.id);
      setPlan(next);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("chat.forecast.rejectFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      {(message || plan.summary) && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {message || plan.summary}
        </Typography>
      )}
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t("chat.forecast.intent")}: {plan.intent || "-"}
        </Typography>
        {!isDocumentSample && (
          <Typography variant="caption" color="text.secondary">
            {t("chat.forecast.method")}: {plan.method || "-"}
          </Typography>
        )}
        {plan.adaptorId ? (
          <>
            <Typography variant="caption" color="text.secondary">
              {t("chat.forecast.adaptor")}: {plan.adaptorName || plan.adaptorId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("chat.forecast.semanticMetric")}: {plan.semanticMetric || "-"}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("chat.forecast.index")}: {plan.indexName || "-"}
          </Typography>
        )}
        {!isDocumentSample && !plan.adaptorId && (
          <Typography variant="caption" color="text.secondary">
            {t("chat.forecast.timeField")}: {plan.timeField || "-"}
          </Typography>
        )}
        {isDocumentSample && plan.sampleSize != null && (
          <Typography variant="caption" color="text.secondary">
            {t("chat.forecast.sampleSize")}: {plan.sampleSize}
          </Typography>
        )}
      </Stack>

      {(pending || rejected || failed) && (
        <>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {t(isDocumentSample ? "chat.forecast.sampleStepsTitle" : "chat.forecast.stepsTitle")}
          </Typography>
          <List dense disablePadding sx={{ mb: 1.5 }}>
            {(plan.steps || []).map((step, idx) => (
              <ListItem key={step.id || idx} alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemText
                  primary={`${idx + 1}. ${step.description || step.kind || "step"}`}
                  secondary={step.detail || undefined}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {pending && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {plan.kvkkNotice
            || t(isDocumentSample ? "chat.forecast.sampleKvkkDefault" : "chat.forecast.kvkkDefault")}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {failed && plan.errorMessage && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {plan.errorMessage}
        </Alert>
      )}

      {rejected && (
        <Alert severity="warning">
          {t(isDocumentSample ? "chat.forecast.sampleRejected" : "chat.forecast.rejected")}
        </Alert>
      )}

      {completed && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {t(isDocumentSample ? "chat.forecast.sampleResultTitle" : "chat.forecast.resultTitle")}
          </Typography>
          {!isDocumentSample && resultModel ? (
            <ForecastResultView model={resultModel} narrativeOverride={plan.aiNarrative} />
          ) : (
            <Typography variant="body2" whiteSpace="pre-wrap">
              {plan.aiNarrative
                || t(isDocumentSample ? "chat.forecast.sampleResultEmpty" : "chat.forecast.resultEmpty")}
            </Typography>
          )}
        </Box>
      )}

      {pending && (
        <Stack direction="row" spacing={1}>
          <AppButton variant="contained" disabled={busy || !plan.id} onClick={() => void onApprove()}>
            {busy
              ? t("chat.forecast.running")
              : t(isDocumentSample ? "chat.forecast.approveSample" : "chat.forecast.approve")}
          </AppButton>
          <AppButton variant="outlined" disabled={busy || !plan.id} onClick={() => void onReject()}>
            {t("chat.forecast.reject")}
          </AppButton>
        </Stack>
      )}
    </Box>
  );
}
